import React from 'react';
import { Airstrip } from '../../types';

type FuelTabProps = {
  airstrips: Airstrip[];
  isLinkingFuel: boolean;
  fuelLinkNode: { code: string; price: number };
  isSyncingFuel: boolean;
  showSyncReview: boolean;
  onOpenLinkFuel: () => void;
  onCloseLinkFuel: () => void;
  onUpdateFuelLinkNode: (updates: Partial<{ code: string; price: number }>) => void;
  onLinkFuelNode: () => void;
  onDecommissionFuelNode: (code: string) => void;
  onUpdateFuelPriceInline: (code: string, price: number) => void;
  onTriggerFuelSync: () => void;
  onCloseSyncReview: () => void;
  onCommitFuelSync: () => void;
};

export const FuelTab: React.FC<FuelTabProps> = ({
  airstrips,
  isLinkingFuel,
  fuelLinkNode,
  isSyncingFuel,
  showSyncReview,
  onOpenLinkFuel,
  onCloseLinkFuel,
  onUpdateFuelLinkNode,
  onLinkFuelNode,
  onDecommissionFuelNode,
  onUpdateFuelPriceInline,
  onTriggerFuelSync,
  onCloseSyncReview,
  onCommitFuelSync,
}) => (
  <div className="space-y-10">
    {isLinkingFuel && (
      <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/80 backdrop-blur-xl p-6">
        <div className="bg-white rounded-[4rem] w-full max-w-xl overflow-hidden shadow-2xl animate-in zoom-in-95">
          <div className="p-10 bg-emerald-600 text-white flex justify-between items-center">
            <div>
              <h3 className="text-3xl font-black uppercase tracking-tight">Link Fuel Node</h3>
              <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-[0.3em] mt-2">Supply Network Authorization</p>
            </div>
            <button onClick={onCloseLinkFuel} className="text-white/40 hover:text-white transition-all">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <div className="p-10 space-y-8">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Select Station</label>
              <select
                className="w-full bg-slate-50 rounded-2xl p-5 font-bold outline-none appearance-none"
                value={fuelLinkNode.code}
                onChange={e => onUpdateFuelLinkNode({ code: e.target.value })}
              >
                <option value="">Choose Airstrip...</option>
                {airstrips.filter(as => !as.fuelAvailable).map(as => (
                  <option key={as.code} value={as.code}>{as.code} - {as.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Initial Price ($/L)</label>
              <input
                type="number"
                step="0.01"
                className="w-full bg-slate-50 rounded-2xl p-5 font-black font-mono outline-none"
                value={fuelLinkNode.price}
                onChange={e => onUpdateFuelLinkNode({ price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <button onClick={onLinkFuelNode} className="w-full py-8 bg-emerald-600 text-white rounded-[2.5rem] font-black text-[10px] uppercase tracking-[0.4em] hover:bg-emerald-500 transition-all shadow-xl">
              Authorize Supply Point
            </button>
          </div>
        </div>
      </div>
    )}

    {showSyncReview && (
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/80 backdrop-blur-md p-6">
        <div className="bg-white rounded-[3.5rem] w-full max-w-2xl overflow-hidden shadow-2xl animate-in zoom-in-95">
          <div className="bg-amber-500 p-10 text-white flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-black uppercase tracking-tight">Market Sync Review</h3>
              <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest mt-1">Source: Puma Energy Aviation Portal</p>
            </div>
          </div>
          <div className="p-10 space-y-6">
            <p className="text-sm font-medium text-slate-500 leading-relaxed">System detected variances in market fuel pricing. Review the proposed adjustments for the Tanzania hub.</p>
            <div className="flex gap-4 pt-4">
              <button onClick={onCloseSyncReview} className="flex-1 py-5 text-[10px] font-black uppercase text-slate-400">Abort</button>
              <button onClick={onCommitFuelSync} className="flex-1 py-5 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black shadow-xl">Commit Sync</button>
            </div>
          </div>
        </div>
      </div>
    )}

    <div className="bg-white rounded-2xl border-2 border-slate-200/50 shadow-xl overflow-hidden backdrop-blur-sm bg-gradient-to-br from-white via-emerald-50/20 to-white">
      <div className="px-8 py-6 border-b-2 border-slate-200/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-emerald-50/40 via-green-50/20 to-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900">Market Fuel Nexus</h3>
            <p className="text-sm text-slate-600 mt-0.5 font-medium">Authorized Supply Network</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <button 
            onClick={onOpenLinkFuel} 
            className="px-4 py-2.5 bg-white text-slate-700 border-2 border-slate-300 rounded-xl text-sm font-semibold hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 transition-all flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Link Station
          </button>
          <button 
            onClick={onTriggerFuelSync} 
            disabled={isSyncingFuel} 
            className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl text-sm font-bold hover:from-emerald-700 hover:to-green-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/40 hover:shadow-xl hover:shadow-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
          >
            {isSyncingFuel ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Syncing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Live Sync
              </>
            )}
          </button>
        </div>
      </div>
      {airstrips.filter(as => as.fuelAvailable).length === 0 ? (
        <div className="p-16 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-100 to-green-100 mb-4 shadow-lg">
            <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className="text-base font-semibold text-slate-800 mb-2">No fuel stations authorized</p>
          <p className="text-sm text-slate-600">Link your first fuel station to get started</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gradient-to-r from-emerald-50 via-green-50/30 to-slate-50 text-slate-700 text-xs font-bold border-b-2 border-slate-200/50">
              <tr>
                <th className="px-6 py-4">Station</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4">Price / L (USD)</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100/50">
              {airstrips.filter(as => as.fuelAvailable).map(as => (
                <tr key={as.code} className="hover:bg-gradient-to-r hover:from-emerald-50/30 hover:via-green-50/20 hover:to-transparent transition-all group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600 to-green-600 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-emerald-500/30">
                        {as.code}
                      </div>
                      <span className="text-sm text-slate-900 font-semibold">{as.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold border-2 bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                      Online
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600 font-mono text-sm font-bold">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="w-24 bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-mono font-bold text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all shadow-sm hover:shadow-md"
                        value={as.fuelPricePerLiter || 1.45}
                        onChange={e => onUpdateFuelPriceInline(as.code, parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => {
                        if (confirm(`Decommission fuel station ${as.code}? This will remove it from the fuel network.`)) {
                          onDecommissionFuelNode(as.code);
                        }
                      }}
                      className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 hover:text-red-800 transition-all border border-red-200 shadow-sm opacity-0 group-hover:opacity-100"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);
